import { useState, useEffect } from 'react';

function BlogAdmin({ adminToken, tabName }) {
    // Form state
    const [title, setTitle] = useState('');
    const [content, setContent] = useState('');
    const [metaDescription, setMetaDescription] = useState('');
    const [ogImageUrl, setOgImageUrl] = useState('');
    const [isPublished, setIsPublished] = useState(true);
    const [personaId, setPersonaId] = useState('');
    const [qaChecked, setQaChecked] = useState(true);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    // Generate-with-Claude state
    const [topic, setTopic] = useState('');
    const [seedNotes, setSeedNotes] = useState('');
    const [generating, setGenerating] = useState(false);

    // Posts management state
    const [posts, setPosts] = useState([]);
    const [showPostsList, setShowPostsList] = useState(false);
    const [loadingPosts, setLoadingPosts] = useState(false);

    // Active personas for the dropdown - fetched once on mount
    const [personas, setPersonas] = useState([]);
    useEffect(() => {
        fetch('/api/admin/personas?active_only=true', {
            headers: { 'Authorization': `Bearer ${adminToken}` },
        })
            .then((res) => (res.ok ? res.json() : []))
            .then(setPersonas)
            .catch(() => setPersonas([]));
    }, [adminToken]);

    // Content coverage review (topics/cities/event series covered, gaps,
    // topic clusters, priority next articles) - see blog_coverage_ai.py
    const [latestCoverageReview, setLatestCoverageReview] = useState(null);
    const [coverageReviewing, setCoverageReviewing] = useState(false);
    const [coverageError, setCoverageError] = useState('');
    const [showCoverageReview, setShowCoverageReview] = useState(false);

    useEffect(() => {
        fetch('/api/admin/blog-posts/coverage-review', {
            headers: { 'Authorization': `Bearer ${adminToken}` },
        })
            .then((res) => (res.ok ? res.json() : null))
            .then(setLatestCoverageReview)
            .catch(() => setLatestCoverageReview(null));
    }, [adminToken]);

    const handleRunCoverageReview = async () => {
        setCoverageReviewing(true);
        setCoverageError('');
        try {
            const response = await fetch('/api/admin/blog-posts/coverage-review', {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${adminToken}` },
            });
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to generate coverage review');
            }
            const review = await response.json();
            setLatestCoverageReview(review);
            setShowCoverageReview(true);
        } catch (err) {
            setCoverageError(err.message);
        } finally {
            setCoverageReviewing(false);
        }
    };

    // Edit mode state
    const [editMode, setEditMode] = useState(false);
    const [editSlug, setEditSlug] = useState('');

    // Load all posts for management, including unpublished drafts awaiting review
    const loadPosts = async () => {
        setLoadingPosts(true);
        try {
            const response = await fetch('/api/admin/blog-posts', {
                headers: { 'Authorization': `Bearer ${adminToken}` },
            });
            if (!response.ok) {
                throw new Error('Failed to fetch posts');
            }
            const postsData = await response.json();
            setPosts(postsData);
            setShowPostsList(true);
            setError('');
        } catch (err) {
            setError('Failed to load posts: ' + err.message);
        } finally {
            setLoadingPosts(false);
        }
    };

    // Load specific post for editing (admin route so drafts can be loaded too)
    const editPost = async (slug) => {
        setLoading(true);
        try {
            const response = await fetch(`/api/admin/blog-posts/${slug}`, {
                headers: { 'Authorization': `Bearer ${adminToken}` },
            });
            if (!response.ok) {
                throw new Error('Failed to load post');
            }
            const post = await response.json();

            setTitle(post.title);
            setContent(post.content);
            setMetaDescription(post.meta_description || '');
            setOgImageUrl(post.og_image_url || '');
            setIsPublished(post.is_published);
            setPersonaId(post.persona_id != null ? String(post.persona_id) : '');
            setQaChecked(post.qa_checked);
            setEditMode(true);
            setEditSlug(slug);
            setShowPostsList(false);
            setError('');
            setSuccess('');
            
            // Scroll to form
            window.scrollTo({ top: 0, behavior: 'smooth' });
        } catch (err) {
            setError('Failed to load post for editing: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    // Cancel edit mode
    const cancelEdit = () => {
        setEditMode(false);
        setEditSlug('');
        setTitle('');
        setContent('');
        setMetaDescription('');
        setOgImageUrl('');
        setIsPublished(true);
        setPersonaId('');
        setQaChecked(true);
        setError('');
        setSuccess('');
    };

    // Generate a draft via the Claude API and load it into the form for
    // review - never saves or publishes anything itself. Forces
    // Published/Fact-checked back off even if they were previously checked,
    // so a freshly generated draft can never skip the review gate.
    const handleGenerate = async () => {
        if (!topic.trim()) {
            setError('Enter a topic to generate a draft');
            return;
        }

        setGenerating(true);
        setError('');
        setSuccess('');

        try {
            const response = await fetch('/api/admin/blog-posts/generate', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${adminToken}`,
                },
                body: JSON.stringify({
                    topic: topic.trim(),
                    seed_notes: seedNotes.trim(),
                    persona_id: personaId ? Number(personaId) : null
                })
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to generate draft');
            }

            const draft = await response.json();
            setTitle(draft.title);
            setContent(draft.content);
            setMetaDescription(draft.meta_description || '');
            setIsPublished(false);
            setQaChecked(false);
            setSuccess('Draft generated - review it below, fact-check against OKGATHER_FACTS.md, then publish when ready.');
            window.scrollTo({ top: 0, behavior: 'smooth' });
        } catch (err) {
            setError(err.message);
        } finally {
            setGenerating(false);
        }
    };

    // Create or update post
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!title.trim() || !content.trim()) {
            setError('Title and content are required');
            return;
        }

        if (isPublished && !qaChecked) {
            setError('This post must be fact-checked against OkGather facts before it can be published - check "Fact-checked" below, or leave "Published" unchecked to save it as a draft.');
            return;
        }

        setLoading(true);
        setError('');
        setSuccess('');

        try {
            const url = editMode ? `/api/blog/posts/${editSlug}` : '/api/blog/posts';
            const method = editMode ? 'PUT' : 'POST';

            const response = await fetch(url, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${adminToken}`,
                },
                body: JSON.stringify({
                    title: title.trim(),
                    content: content.trim(),
                    meta_description: metaDescription.trim(),
                    og_image_url: ogImageUrl.trim(),
                    is_published: isPublished,
                    persona_id: personaId ? Number(personaId) : null,
                    qa_checked: qaChecked
                })
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || `Failed to ${editMode ? 'update' : 'create'} post`);
            }

            setSuccess(editMode ? 'Post updated successfully!' : (isPublished ? 'Post created and published!' : 'Draft saved - not visible on the site until published.'));

            // Clear form and exit edit mode
            setTitle('');
            setContent('');
            setMetaDescription('');
            setOgImageUrl('');
            setIsPublished(true);
            setPersonaId('');
            setQaChecked(true);
            setEditMode(false);
            setEditSlug('');

            // Refresh posts list if it's showing
            if (showPostsList) {
                loadPosts();
            }

        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    // Delete post
    const deletePost = async (slug, title) => {
        if (!window.confirm(`Are you sure you want to delete "${title}"? This cannot be undone.`)) {
            return;
        }

        try {
            const response = await fetch(`/api/blog/posts/${slug}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${adminToken}`,
                },
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to delete post');
            }

            setSuccess('Post deleted successfully!');
            
            // Refresh posts list
            if (showPostsList) {
                loadPosts();
            }
            
            // If we were editing this post, clear the form
            if (editMode && editSlug === slug) {
                cancelEdit();
            }

        } catch (err) {
            setError('Failed to delete post: ' + err.message);
        }
    };

    return (
        <div style={{ maxWidth: '900px', margin: '0 auto', padding: '20px' }}>
            <h3>{tabName}</h3>

            {/* Off-the-shelf SEO tooling reference - not integrated with anything below,
                just links for whoever's planning content/keywords. Not evaluated against
                our workflow yet - may or may not be worth adopting. */}
            <div style={{ marginBottom: '20px', padding: '15px 20px', border: '1px solid #d6c8f0', borderRadius: '8px', backgroundColor: '#faf8ff' }}>
                <p style={{ margin: '0 0 8px 0', fontSize: '13px', fontWeight: 'bold', color: '#333' }}>
                    SEO tools (not integrated - reference only)
                </p>
                <p style={{ margin: 0, fontSize: '13px', color: '#666' }}>
                    <a href="https://ahrefs.com/" target="_blank" rel="noopener noreferrer">Ahrefs</a> - keyword
                    research, backlink/competitor analysis, technical site audits. Complements this workflow rather
                    than overlapping it (research vs. drafting).{' '}
                    <a href="https://www.outrank.so/" target="_blank" rel="noopener noreferrer">Outrank</a> - AI
                    programmatic content tool by indie maker Tibo Louis-Lucas: keyword research plus auto-writes and
                    auto-publishes articles on a schedule (~$99/mo for ~30 articles). Overlaps with what this tab
                    already does (draft + review); could replace or complement it - not evaluated against our
                    persona/fact-check workflow yet.
                </p>
            </div>

            {/* Google Search Console - free, real search-performance data.
                This is what actually validates (or corrects) the AI coverage
                review's qualitative keyword notes below, since neither Claude
                nor this admin panel has real search-volume/ranking data. */}
            <div style={{ marginBottom: '20px', padding: '15px 20px', border: '1px solid #d6c8f0', borderRadius: '8px', backgroundColor: '#faf8ff' }}>
                <p style={{ margin: '0 0 8px 0', fontSize: '13px', fontWeight: 'bold', color: '#333' }}>
                    <a href="https://search.google.com/search-console" target="_blank" rel="noopener noreferrer">Google Search Console</a>
                    {' '}(free - measure how SEO is actually performing)
                </p>
                <p style={{ margin: 0, fontSize: '13px', color: '#666' }}>
                    Free, and the only source of real search data here (everything else on this page is either
                    AI judgment or a paid tool we haven't adopted). What to check periodically:
                </p>
                <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px', fontSize: '13px', color: '#666' }}>
                    <li><strong>Coverage/Indexing report</strong> - confirms blog posts and theme landing pages are
                        actually indexed, not just crawlable. Worth checking after any SSR-related change.</li>
                    <li><strong>Performance {'>'} Queries</strong> - real queries pages get impressions for. Look
                        especially for queries with impressions but low clicks/position past page 1 - that's a
                        concrete keyword-targeting gap, more reliable than the AI review's qualitative guess below.</li>
                    <li><strong>Performance {'>'} Pages</strong> - which specific posts/landing pages get real
                        traffic vs. none - the ground truth for which topic clusters/event series are actually
                        resonating, to cross-check against the coverage review below.</li>
                    <li><strong>URL Inspection tool</strong> - spot-check a specific URL's indexed status and see
                        how Google actually rendered it.</li>
                    <li><strong>Sitemaps report</strong> - confirms <code>/sitemap.xml</code> was fetched and
                        processed without errors.</li>
                </ul>
            </div>

            {/* Content Coverage Review: which event series/cities have
                supporting articles, topic cluster health, and priority next
                articles - see blog_coverage_ai.py */}
            <div style={{ marginBottom: '30px', padding: '20px', border: '1px solid #d6c8f0', borderRadius: '8px', backgroundColor: '#faf8ff' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                    <div>
                        <h4 style={{ margin: '0 0 4px 0' }}>Content Coverage Review</h4>
                        <p style={{ margin: 0, fontSize: '13px', color: '#666' }}>
                            {latestCoverageReview
                                ? `Last reviewed: ${new Date(latestCoverageReview.created_at).toLocaleString()}`
                                : 'Not reviewed yet.'}
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={handleRunCoverageReview}
                        disabled={coverageReviewing}
                        style={{
                            padding: '10px 20px',
                            backgroundColor: coverageReviewing ? '#ccc' : '#6f42c1',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: coverageReviewing ? 'not-allowed' : 'pointer',
                            fontSize: '14px',
                            fontWeight: 'bold'
                        }}
                    >
                        {coverageReviewing ? 'Reviewing...' : 'Review Coverage'}
                    </button>
                </div>

                {coverageError && (
                    <div style={{
                        marginTop: '15px', color: 'red', padding: '10px',
                        border: '1px solid red', borderRadius: '4px', backgroundColor: '#ffe6e6', fontSize: '13px'
                    }}>
                        {coverageError}
                    </div>
                )}

                {latestCoverageReview && (
                    <div style={{ marginTop: '15px' }}>
                        <button
                            type="button"
                            onClick={() => setShowCoverageReview((v) => !v)}
                            style={{
                                padding: '5px 15px', backgroundColor: '#6c757d', color: 'white',
                                border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '13px'
                            }}
                        >
                            {showCoverageReview ? 'Hide review' : 'Show review'}
                        </button>

                        {showCoverageReview && (
                            <div style={{ marginTop: '15px' }}>
                                <p style={{ fontSize: '14px', color: '#333' }}>
                                    {latestCoverageReview.review_data.overall_summary}
                                </p>

                                {latestCoverageReview.review_data.priority_next_articles?.length > 0 && (
                                    <div style={{ marginBottom: '15px' }}>
                                        <p style={{ fontSize: '13px', fontWeight: 'bold', margin: '0 0 5px 0' }}>Priority next articles</p>
                                        {latestCoverageReview.review_data.priority_next_articles.map((a, i) => (
                                            <div key={i} style={{ borderTop: '1px solid #eee', padding: '8px 0' }}>
                                                <p style={{ margin: '0 0 3px 0', fontSize: '13px' }}>
                                                    <strong>[{a.priority}]</strong> {a.title_idea}
                                                    {a.target_meetup_urlname ? ` — targets ${a.target_meetup_urlname}` : ''}
                                                    {a.target_city ? ` (${a.target_city})` : ''}
                                                </p>
                                                <p style={{ margin: 0, fontSize: '12px', color: '#666' }}>{a.rationale}</p>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {latestCoverageReview.review_data.event_series_coverage?.length > 0 && (
                                    <div style={{ marginBottom: '15px' }}>
                                        <p style={{ fontSize: '13px', fontWeight: 'bold', margin: '0 0 5px 0' }}>Event series coverage</p>
                                        {latestCoverageReview.review_data.event_series_coverage.map((s, i) => (
                                            <div key={i} style={{ borderTop: '1px solid #eee', padding: '8px 0' }}>
                                                <p style={{ margin: '0 0 3px 0', fontSize: '13px' }}>
                                                    <strong>[{s.coverage_level}]</strong> {s.meetup_urlname}
                                                    {s.theme_name ? ` — ${s.theme_name}` : ''}{s.city ? ` (${s.city})` : ''}
                                                </p>
                                                {s.notes && <p style={{ margin: 0, fontSize: '12px', color: '#666' }}>{s.notes}</p>}
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {latestCoverageReview.review_data.topic_clusters?.length > 0 && (
                                    <div style={{ marginBottom: '15px' }}>
                                        <p style={{ fontSize: '13px', fontWeight: 'bold', margin: '0 0 5px 0' }}>Topic clusters</p>
                                        {latestCoverageReview.review_data.topic_clusters.map((c, i) => (
                                            <div key={i} style={{ borderTop: '1px solid #eee', padding: '8px 0' }}>
                                                <p style={{ margin: '0 0 3px 0', fontSize: '13px' }}>
                                                    <strong>[{c.health}]</strong> {c.cluster_name} ({c.post_slugs?.join(', ')})
                                                </p>
                                                {c.notes && <p style={{ margin: 0, fontSize: '12px', color: '#666' }}>{c.notes}</p>}
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {latestCoverageReview.review_data.keyword_targeting_notes?.length > 0 && (
                                    <div>
                                        <p style={{ fontSize: '13px', fontWeight: 'bold', margin: '0 0 5px 0' }}>Keyword-targeting notes</p>
                                        <ul style={{ fontSize: '13px', color: '#333', margin: 0, paddingLeft: '20px' }}>
                                            {latestCoverageReview.review_data.keyword_targeting_notes.map((n, i) => <li key={i}>{n}</li>)}
                                        </ul>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Posts Management Section */}
            <div style={{ marginBottom: '30px', padding: '20px', border: '1px solid #ddd', borderRadius: '8px', backgroundColor: '#f8f9fa' }}>
                <h4 style={{ margin: '0 0 15px 0' }}>Manage Posts</h4>
                
                {!showPostsList ? (
                    <button 
                        onClick={loadPosts}
                        disabled={loadingPosts}
                        style={{
                            padding: '10px 20px',
                            backgroundColor: '#28a745',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: loadingPosts ? 'not-allowed' : 'pointer',
                            fontSize: '14px'
                        }}
                    >
                        {loadingPosts ? 'Loading...' : 'Show All Posts'}
                    </button>
                ) : (
                    <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                            <span style={{ fontWeight: 'bold' }}>Posts ({posts.length})</span>
                            <button 
                                onClick={() => setShowPostsList(false)}
                                style={{ 
                                    padding: '5px 15px', 
                                    backgroundColor: '#6c757d', 
                                    color: 'white', 
                                    border: 'none', 
                                    borderRadius: '4px',
                                    cursor: 'pointer'
                                }}
                            >
                                Hide
                            </button>
                        </div>
                        
                        {posts.length === 0 ? (
                            <p style={{ color: '#666', margin: '20px 0' }}>No posts found.</p>
                        ) : (
                            <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                                {posts.map(post => (
                                    <div key={post.id} style={{
                                        border: '1px solid #e0e0e0', 
                                        padding: '15px', 
                                        margin: '10px 0',
                                        borderRadius: '4px',
                                        backgroundColor: 'white'
                                    }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                                            <h5 style={{ margin: 0, fontSize: '16px' }}>{post.title}</h5>
                                            <span style={{
                                                padding: '2px 8px',
                                                borderRadius: '10px',
                                                fontSize: '11px',
                                                fontWeight: 'bold',
                                                backgroundColor: post.is_published ? '#d4edda' : '#f8d7da',
                                                color: post.is_published ? '#155724' : '#721c24'
                                            }}>
                                                {post.is_published ? 'Published' : 'Draft'}
                                            </span>
                                            {!post.qa_checked && (
                                                <span style={{
                                                    padding: '2px 8px',
                                                    borderRadius: '10px',
                                                    fontSize: '11px',
                                                    fontWeight: 'bold',
                                                    backgroundColor: '#fff3cd',
                                                    color: '#856404'
                                                }}>
                                                    Needs fact-check
                                                </span>
                                            )}
                                            {post.persona_first_name && (
                                                <span style={{ fontSize: '12px', color: '#666' }}>
                                                    By {post.persona_first_name}
                                                </span>
                                            )}
                                        </div>
                                        <p style={{ color: '#666', margin: '0 0 10px 0', fontSize: '14px' }}>
                                            {post.excerpt}
                                        </p>
                                        <p style={{ color: '#999', margin: '0 0 15px 0', fontSize: '12px' }}>
                                            Created: {new Date(post.created_at).toLocaleDateString()}
                                            {post.updated_at && post.updated_at !== post.created_at && (
                                                <span> • Updated: {new Date(post.updated_at).toLocaleDateString()}</span>
                                            )}
                                        </p>
                                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                            <button 
                                                onClick={() => editPost(post.slug)}
                                                style={{
                                                    padding: '5px 15px',
                                                    backgroundColor: '#007bff',
                                                    color: 'white',
                                                    border: 'none',
                                                    borderRadius: '4px',
                                                    cursor: 'pointer',
                                                    fontSize: '12px'
                                                }}
                                            >
                                                Edit
                                            </button>
                                            {post.is_published && (
                                                <a
                                                    href={`/blog/${post.slug}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    style={{
                                                        padding: '5px 15px',
                                                        backgroundColor: '#28a745',
                                                        color: 'white',
                                                        textDecoration: 'none',
                                                        borderRadius: '4px',
                                                        fontSize: '12px',
                                                        display: 'inline-block'
                                                    }}
                                                >
                                                    View
                                                </a>
                                            )}
                                            <button 
                                                onClick={() => deletePost(post.slug, post.title)}
                                                style={{
                                                    padding: '5px 15px',
                                                    backgroundColor: '#dc3545',
                                                    color: 'white',
                                                    border: 'none',
                                                    borderRadius: '4px',
                                                    cursor: 'pointer',
                                                    fontSize: '12px'
                                                }}
                                            >
                                                Delete
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Edit Mode Indicator */}
            {editMode && (
                <div style={{
                    backgroundColor: '#fff3cd',
                    border: '1px solid #ffeaa7',
                    padding: '15px',
                    borderRadius: '4px',
                    marginBottom: '20px'
                }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                            <strong>✏️ Editing Mode:</strong> {title || 'Loading...'}
                        </div>
                        <button 
                            onClick={cancelEdit}
                            style={{
                                padding: '8px 15px',
                                backgroundColor: '#6c757d',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer'
                            }}
                        >
                            Cancel Edit
                        </button>
                    </div>
                </div>
            )}

            {/* Generate Draft with Claude */}
            <div style={{ marginBottom: '30px', padding: '20px', border: '1px solid #d6c8f0', borderRadius: '8px', backgroundColor: '#faf8ff' }}>
                <h4 style={{ margin: '0 0 5px 0' }}>Generate Draft with Claude</h4>
                <p style={{ color: '#666', fontSize: '13px', margin: '0 0 15px 0' }}>
                    Fills in the title/content/meta description below for you to review - it does not save or publish
                    anything. You still need to fact-check and tick both boxes further down before it can go live.
                </p>

                <div style={{ marginBottom: '15px' }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', fontSize: '14px' }}>
                        Persona:
                    </label>
                    <select
                        value={personaId}
                        onChange={(e) => setPersonaId(e.target.value)}
                        style={{
                            width: '100%',
                            padding: '10px',
                            border: '1px solid #ccc',
                            borderRadius: '4px',
                            fontSize: '14px',
                            boxSizing: 'border-box'
                        }}
                        disabled={generating}
                    >
                        <option value="">No persona (OkGather's own voice)</option>
                        {personas.map((persona) => (
                            <option key={persona.id} value={persona.id}>
                                {persona.name} (byline: "{persona.public_first_name}")
                            </option>
                        ))}
                    </select>
                </div>

                <div style={{ marginBottom: '15px' }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', fontSize: '14px' }}>
                        Topic:
                    </label>
                    <input
                        type="text"
                        value={topic}
                        onChange={(e) => setTopic(e.target.value)}
                        placeholder="e.g. Why in-person events beat swiping for meeting people"
                        style={{
                            width: '100%',
                            padding: '10px',
                            border: '1px solid #ccc',
                            borderRadius: '4px',
                            fontSize: '14px',
                            boxSizing: 'border-box'
                        }}
                        disabled={generating}
                    />
                </div>

                <div style={{ marginBottom: '15px' }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', fontSize: '14px' }}>
                        Real experience / notes to draw on (optional, recommended):
                    </label>
                    <textarea
                        value={seedNotes}
                        onChange={(e) => setSeedNotes(e.target.value)}
                        placeholder="An actual anecdote, opinion, or detail to seed the draft with - keeps it from reading as generic AI content."
                        rows={3}
                        style={{
                            width: '100%',
                            padding: '10px',
                            border: '1px solid #ccc',
                            borderRadius: '4px',
                            fontSize: '14px',
                            lineHeight: '1.5',
                            boxSizing: 'border-box'
                        }}
                        disabled={generating}
                    />
                </div>

                <button
                    type="button"
                    onClick={handleGenerate}
                    disabled={generating}
                    style={{
                        padding: '10px 20px',
                        backgroundColor: generating ? '#ccc' : '#6f42c1',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: generating ? 'not-allowed' : 'pointer',
                        fontSize: '14px',
                        fontWeight: 'bold'
                    }}
                >
                    {generating ? 'Generating...' : 'Generate Draft'}
                </button>
            </div>

            {/* Error/Success Messages */}
            {error && (
                <div style={{
                    color: 'red',
                    padding: '10px',
                    marginBottom: '20px',
                    border: '1px solid red',
                    borderRadius: '4px',
                    backgroundColor: '#ffe6e6'
                }}>
                    {error}
                </div>
            )}

            {success && (
                <div style={{
                    color: 'green',
                    padding: '10px',
                    marginBottom: '20px',
                    border: '1px solid green',
                    borderRadius: '4px',
                    backgroundColor: '#e6ffe6'
                }}>
                    {success}
                </div>
            )}

            {/* Blog Post Form */}
            <form onSubmit={handleSubmit}>
                <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                        Title:
                    </label>
                    <input
                        type="text"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        placeholder="Enter blog post title"
                        style={{
                            width: '100%',
                            padding: '10px',
                            border: '1px solid #ccc',
                            borderRadius: '4px',
                            fontSize: '16px',
                            boxSizing: 'border-box'
                        }}
                        disabled={loading}
                    />
                </div>

                <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                        Persona (optional):
                    </label>
                    <select
                        value={personaId}
                        onChange={(e) => setPersonaId(e.target.value)}
                        style={{
                            width: '100%',
                            padding: '10px',
                            border: '1px solid #ccc',
                            borderRadius: '4px',
                            fontSize: '14px',
                            boxSizing: 'border-box'
                        }}
                        disabled={loading}
                    >
                        <option value="">No persona (published as OkGather)</option>
                        {personas.map((persona) => (
                            <option key={persona.id} value={persona.id}>
                                {persona.name} (byline: "{persona.public_first_name}")
                            </option>
                        ))}
                    </select>
                    <small style={{ color: '#666', fontSize: '12px' }}>
                        Manage personas under the Personas tab. Selecting one attributes this post to them - only their
                        public first name is shown as the byline.
                    </small>
                </div>

                <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                        Content (Markdown):
                    </label>
                    <textarea
                        value={content}
                        onChange={(e) => setContent(e.target.value)}
                        placeholder="Write your blog post content in Markdown format..."
                        rows={20}
                        style={{
                            width: '100%',
                            padding: '10px',
                            border: '1px solid #ccc',
                            borderRadius: '4px',
                            fontSize: '14px',
                            fontFamily: 'Monaco, Consolas, monospace',
                            lineHeight: '1.5',
                            boxSizing: 'border-box'
                        }}
                        disabled={loading}
                    />
                    <small style={{ color: '#666', fontSize: '12px' }}>
                        Use Markdown syntax: **bold**, *italic*, # Heading, etc.
                    </small>
                </div>

                <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                        Meta Description (optional):
                    </label>
                    <textarea
                        value={metaDescription}
                        onChange={(e) => setMetaDescription(e.target.value)}
                        placeholder="Custom description for search engines and social previews..."
                        rows={2}
                        maxLength={300}
                        style={{
                            width: '100%',
                            padding: '10px',
                            border: '1px solid #ccc',
                            borderRadius: '4px',
                            fontSize: '14px',
                            lineHeight: '1.5',
                            boxSizing: 'border-box'
                        }}
                        disabled={loading}
                    />
                    <small style={{ color: '#666', fontSize: '12px' }}>
                        Shown in Google/social previews and used in the page's structured data. Leave blank to auto-generate from the first 160 characters of the content.
                    </small>
                </div>

                <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                        Social/OG Image URL (optional):
                    </label>
                    <input
                        type="text"
                        value={ogImageUrl}
                        onChange={(e) => setOgImageUrl(e.target.value)}
                        placeholder="https://okgather.com/images/your-image.jpg"
                        style={{
                            width: '100%',
                            padding: '10px',
                            border: '1px solid #ccc',
                            borderRadius: '4px',
                            fontSize: '14px',
                            boxSizing: 'border-box'
                        }}
                        disabled={loading}
                    />
                    <small style={{ color: '#666', fontSize: '12px' }}>
                        Leave blank to use the site's default preview image.
                    </small>
                </div>

                <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 'bold', cursor: 'pointer' }}>
                        <input
                            type="checkbox"
                            checked={qaChecked}
                            onChange={(e) => setQaChecked(e.target.checked)}
                            disabled={loading}
                        />
                        Fact-checked against OkGather facts
                    </label>
                    <small style={{ color: '#666', fontSize: '12px' }}>
                        Required before this post can be published. Check every claim about OkGather itself (pricing,
                        perks, how it works, what it is/isn't) against OKGATHER_FACTS.md before ticking this - this is
                        specifically the hallucination check for AI-assisted drafts, not a general proofread.
                    </small>
                </div>

                <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 'bold', cursor: 'pointer' }}>
                        <input
                            type="checkbox"
                            checked={isPublished}
                            onChange={(e) => setIsPublished(e.target.checked)}
                            disabled={loading}
                        />
                        Published
                    </label>
                    <small style={{ color: '#666', fontSize: '12px' }}>
                        Uncheck to save as a draft - it won't be visible on the site until you check this and save again. Use this to review AI-assisted content before it goes live.
                    </small>
                </div>

                <button
                    type="submit"
                    disabled={loading}
                    style={{
                        padding: '12px 24px',
                        backgroundColor: loading ? '#ccc' : (editMode ? '#28a745' : '#007bff'),
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: loading ? 'not-allowed' : 'pointer',
                        fontSize: '16px',
                        fontWeight: 'bold'
                    }}
                >
                    {loading
                        ? (editMode ? 'Updating...' : 'Saving...')
                        : (editMode
                            ? (isPublished ? 'Update Post' : 'Save Draft')
                            : (isPublished ? 'Create Post' : 'Save Draft'))}
                </button>
            </form>
        </div>
    );
}

export default BlogAdmin;